<template>
	<app-header-teleport>
		<div class="flex flex-1 flex-wrap justify-between">
			<div class="flex items-center gap-3">
				<app-title :title="$t('orders')" />
				<div>
					<app-button
						v-if="isManagingOneBusiness"
						icon="plus"
						class="btn btn-primary transparent small ml-2"
						@click="createOrder"
					/>
					<app-dropdown-button
						v-else
						:label="$t('add_order')"
						size="small"
						type="primary"
						label-prop="restaurantName"
						:items="user.restaurants"
						@click="(item) => createOrder(item.restaurantID)"
					/>
				</div>
				<app-button
					v-if="![21, 24].includes(user.type)"
					icon="download"
					class="btn btn-primary transparent small"
					@click="exportOrders"
					:loading="exporting"
				/>
				<app-button
					icon="arrows-cw"
					class="btn btn-success transparent small"
					@click="refresh"
					:loading="loading || refreshing"
				/>
				<div>
					<app-advanced-filter
						@onFilter="onFilter"
						@onClear="onClear"
						@onClick="onClick"
						:loading="loading || refreshing"
						v-if="loaded"
					>
						<div v-if="renderFilters">
							<app-city-select id="cityFilter" v-model="filters.cityId" />
							<app-input type="text" name="Name" v-model="filters.orderId" :placeholder="$t('order')" />
							<app-advanced-date-filter
								v-model="filters"
								:start-date="'startDate'"
								:end-date="'endDate'"
								:enable-time="true"
							/>
							<app-select
								class="w-full"
								:placeholder="$t('paymentMethod')"
								:options="paymentMethodsOptions"
								v-model="filters.paymentType"
							/>
							<app-checkbox
								:label="$t('include_completed_orders')"
								v-model="filters.includeCompletedOrders"
							/>
						</div>
						<div v-else />
					</app-advanced-filter>
				</div>
				<app-button
					v-if="
						extraDeliveryBusiness &&
							!extraDeliveryBusiness.extraDeliveryTime.extraTime &&
							canEditExtraDeliveryTime
					"
					icon="clock"
					class="btn btn-primary transparent small"
					@click="showDelayModal = true"
				/>
				<div
					class="flex items-center gap-2 mx-1"
					v-if="extraDeliveryBusiness && extraDeliveryBusiness.extraDeliveryTime.extraTime"
				>
					<div
						class="flex items-center btn btn-primary cursor-pointer rounded-md px-4 gap-2"
						style="width: fit-content"
						@click="showDelayModal = true"
					>
						<app-icon icon="attention" class="text-white animate-pulse" />
						<span class="text-white animate-pulse"
							>{{ $t('delay') }}: +{{ extraDeliveryBusiness.extraDeliveryTime.extraTime }} min
							{{ $t('until') }}
							{{ $filters.getShortTime(extraDeliveryBusiness.extraDeliveryTime.endDate) }}</span
						>
					</div>
				</div>
			</div>
			<app-digital-time />
		</div>
	</app-header-teleport>
	<div class="flex flex-1">
		<app-group-card-filter
			v-model="filters.orderStatus"
			:filters="statusFilters"
			@change="filterByStatus"
			:statistics="statistics"
		/>
	</div>
	<app-section class="tight overflow-x-auto">
		<div class="hidden sm:flex items-center mt-2" v-if="loaded && renderFilters">
			<app-icon icon="filters" class="mr-1" />
			<span class="mr-2">{{ $t('quick_filters') }}:</span>
			<app-city-select
				id="quickCity"
				class="w-32 mr-1"
				:modelValue="filters.cityId"
				@update:modelValue="(val) => updateFilter('cityId', val)"
			/>
			<app-select
				class="mr-1 w-48"
				:placeholder="$t('delivery_type')"
				:can-deselect="false"
				:options="takeawayOptions"
				v-model="filters.takeaway"
				@select="updateFilter('takeaway', $event)"
			/>
		</div>
		<orders-table
			:orders="orders"
			@onOrderUpdate="onOrderUpdate"
			@onStatusChange="changeStatus"
			@updateLocalOrder="updateLocalOrder"
			:loaders="orderLoaders"
		/>
		<app-pagination
			:filters="filters"
			@prevPage="prevPage"
			@nextPage="nextPage"
			@limitChange="changeLimit"
			@loadMore="loadMore"
			:length="orders.length"
			:loading="loading || refreshing"
		/>
	</app-section>
	<extra-delivery-time-modal
		v-if="showDelayModal && canEditExtraDeliveryTime"
		:business="extraDeliveryBusiness"
		@close="showDelayModal = false"
	/>
</template>

<script>
	import constants from '@/constants/orders';
	import AppGroupCardFilter from '@/components/app/AppGroupCardFilter';
	import RouteNames from '@/router/names';
	import OrderService from '@/services/OrderService';
	import AppDigitalTime from '@/components/app/AppDigitalTime';
	import AppAdvancedFilter from '@/components/app/AppAdvancedFilter';
	import AppAdvancedDateFilter from '@/components/app/AppAdvancedDateFilter';
	import AppPagination from '@/components/app/AppPagination';
	import OrdersTable from '@/components/orders/OrdersTable';
	import AppIcon from '@/components/app/AppIcon';
	import { calcProgressPercentage } from '@/services/UtilityService';
	import AppCitySelect from '@/components/app/AppCitySelect';
	import { mapGetters } from 'vuex';
	import AppDropdownButton from '@/components/app/AppDropdownButton';
	import ExtraDeliveryTimeModal from '@/components/ExtraDeliveryTimeModal.vue';
	import BusinessService from '@/services/sales/BusinessService';
	import moment from 'moment';
	import AppCheckbox from '@/components/app/AppCheckbox.vue';

	export default {
		name: 'OrderList',
		components: {
			AppCheckbox,
			ExtraDeliveryTimeModal,
			AppDropdownButton,
			AppCitySelect,
			AppDigitalTime,
			AppGroupCardFilter,
			AppAdvancedFilter,
			AppAdvancedDateFilter,
			AppPagination,
			OrdersTable,
			AppIcon,
		},
		computed: {
			...mapGetters({
				user: 'user',
				selectedRestaurants: 'selectedRestaurants',
				posSettings: 'posSettings',
			}),
			isManagingOneBusiness() {
				return this.user?.restaurants?.length === 1;
			},
			extraDeliveryBusiness() {
				return this.user.restaurants?.length === 1
					? this.user.restaurants[0]
					: this.user.restaurants?.find((rest) => {
							return rest.extraDeliveryTime.extraTime >= 0;
					  }) || this.user.restaurants[0];
			},
			canEditExtraDeliveryTime() {
				return BusinessService.canEditExtraDeliveryTime();
			},
		},
		data() {
			return {
				RouteNames,
				selectedOrder: null,
				getOrdersInterval: null,
				statusFilters: null,
				filters: { ...constants.defaultFilters },
				searchFilters: { ...constants.defaultFilters },
				loading: false,
				loaded: false, // Added due to params transformation
				refreshing: false,
				columns: constants.columns,
				statistics: {},
				orders: [],
				ordersDriver: [],
				loadingRestaurants: false,
				loadingCustomers: false,
				loadingCouriers: false,
				exporting: false,
				orderLoaders: {},
				renderFilters: true,
				takeawayOptions: [...constants.deliveryTypesOptions],
				refreshTime: 90000,
				showDelayModal: false,
				paymentMethodsOptions: [],
			};
		},
		methods: {
			onNotesClick(order) {
				this.selectedOrder = order;
			},
			exportOrders() {
				this.exporting = true;
				OrderService.export(this.orders).finally(() => {
					this.exporting = false;
				});
			},
			getOrders(propToLoad = 'loading', loadMore = false) {
				this[propToLoad] = true;
				this.$router.push({ query: this.searchFilters });
				if (propToLoad === 'loading') {
					this.emitter.emit('isLoading', true);
				}
				let payload = { ...this.searchFilters, businessIDs: this.selectedRestaurants.join('-') };
				if (this.searchFilters.orderStatus === 'CREDIT_CARD') {
					payload.payedWithCard = 1;
					delete payload.orderStatus;
				}
				if (this.searchFilters.orderStatus === 'CASHLESS_VALUE') {
					delete payload.orderStatus;
				}
				if (payload.includeCompletedOrders == 0) {
					payload.includeCompletedOrders = -1;
				}
				if (this.user.id) {
					OrderService.dashboard(payload)
						.then(({ orders, statistics }) => {
							if (loadMore) {
								if (!orders.length) this.$notificationService.info('No more items to load');
								this.orders = this.orders.concat(orders);
							} else this.orders = orders;
							this.statistics = statistics;
							this.setTextValues();
						})
						.catch((e) => {
							this.$notificationService.error(e);
						})
						.finally(() => {
							if (this.loading) this.emitter.emit('isLoading', false);
							this[propToLoad] = false;
						});
				}
			},
			getPaymentMethodsOptions() {
				this.loading = true;
				OrderService.getOrderPaymentType()
					.then((payments) => {
						payments.forEach((payment) =>
							this.paymentMethodsOptions.push({
								label: this.$filters.capitalization(payment.description),
								value: payment.id,
							})
						);
					})
					.catch((err) => {
						this.$notificationService.error(err);
					})
					.finally(() => {
						this.loading = false;
					});
			},
			calcProgressPercentage,
			setTextValues() {
				this.statusFilters.map((filter) => {
					filter.text = this.statistics[filter.propKey] ? this.statistics[filter.propKey] : 0;
					filter.comparison = this.calcProgressPercentage(
						this.statistics[filter.propKey],
						this.statistics.compared[filter.propKey]
					);
					filter.creditCardData = { totalValue: this.statistics.totalCreditCardOrdersValue };
				});
			},
			createOrder(businessId = this.user.restaurants[0].restaurantID) {
				this.$router.push({ name: RouteNames.CreateOrder, params: { businessId } });
			},
			filterByStatus() {
				this.searchFilters.orderStatus = this.filters.orderStatus;
				this.searchFilters.offset = 0;
				this.filterCashlessValue();
				this.refresh();
			},
			filterCashlessValue() {
				this.searchFilters.withCreditCardOrCashless =
					this.searchFilters.orderStatus === 'CASHLESS_VALUE' ? 1 : -1;
			},
			parseIntQueryParams() {
				this.searchFilters.offset = parseInt(this.searchFilters.offset);
				this.searchFilters.limit = parseInt(this.searchFilters.limit);
				this.searchFilters.restaurantId = this.searchFilters.restaurantId
					? parseInt(this.searchFilters.restaurantId)
					: null;
				this.searchFilters.customerId = this.searchFilters.customerId
					? parseInt(this.searchFilters.customerId)
					: null;
				this.searchFilters.courierId = this.searchFilters.courierId
					? parseInt(this.searchFilters.courierId)
					: null;
			},
			prevPage() {
				this.parseIntQueryParams();
				this.searchFilters.offset = Math.max(this.searchFilters.offset - this.searchFilters.limit, 0);
				this.getOrders('refreshing');
			},
			nextPage() {
				this.parseIntQueryParams();
				this.searchFilters.offset = this.searchFilters.offset + this.searchFilters.limit;
				this.getOrders('refreshing');
			},
			changeLimit(value) {
				this.parseIntQueryParams();
				this.searchFilters = {
					...this.searchFilters,
					limit: value,
					offset: 0,
				};
				this.filters = { ...this.searchFilters };
				this.getOrders('refreshing');
			},
			loadMore() {
				this.searchFilters.offset = this.searchFilters.offset + this.searchFilters.limit;
				this.getOrders('refreshing', true);
			},
			refresh() {
				this.getOrders('refreshing');
			},
			onFilter() {
				this.searchFilters = { ...this.filters };
				this.refresh();
				this.reRenderFilters();
			},
			onClear() {
				this.filters = { ...constants.defaultFilters };
				this.searchFilters = { ...constants.defaultFilters };
				this.refresh();
			},
			onClick() {
				if (!this.paymentMethodsOptions.length) {
					this.getPaymentMethodsOptions();
				}
			},
			onOrderUpdate(orderId) {
				this.orderLoaders[orderId] = true;
				OrderService.getOrder(orderId)
					.then((order) => {
						let selectedOrderIndex = this.orders.findIndex(({ orderID }) => orderID === order['orderID']);
						this.orders[selectedOrderIndex] = order;
					})
					.finally(() => {
						this.orderLoaders[orderId] = false;
					});
			},
			changeStatus(orderId, status) {
				this.$store.dispatch('setLoadingOrderId', orderId);
				this.orderLoaders[orderId] = true;
				OrderService.changeOrderStatus(orderId, status)
					.then(() => {
						this.onOrderUpdate(orderId);
					})
					.catch((e) => this.$notificationService.error(e))
					.finally(() => {
						this.orderLoaders[orderId] = false;
						this.$store.dispatch('setLoadingOrderId', null);
					});
			},
			updateLocalOrder({ orderId, props }) {
				const i = this.orders.findIndex((i) => i.orderID === orderId);
				this.orders[i] = {
					...this.orders[i],
					...props,
				};
			},
			updateFilter(key, val) {
				this.filters[key] = val;
				this.onFilter();
			},
			reRenderFilters() {
				this.renderFilters = false;
				this.$nextTick(() => {
					this.renderFilters = true;
				});
			},
			onNewOrderReceived() {},
			getStatus() {
				if (this.posSettings?.enableCookingTime) {
					this.statusFilters = constants.statusForCookingTime;
				} else if (this.user?.restaurants?.some((restaurant) => restaurant.baboonCourier === 1)) {
					this.statusFilters = constants.status;
				} else {
					if (this.user?.restaurants?.some((restaurant) => restaurant.takeAwayModule === 1)) {
						this.statusFilters = constants.statusShort;
					} else {
						this.statusFilters = constants.statusShort.filter((status) => status.propKey !== 'cooked');
					}
				}
			},
		},
		beforeUnmount() {
			clearInterval(this.getOrdersInterval);
			this.emitter.off('onNewOrderReceived', null);
			this.emitter.off('updateLocalOrder', null);
		},
		mounted() {
			this.getStatus();
			if (Object.keys(this.$route.query).length > 0) {
				let filters = Object.assign(constants.defaultFilters, this.$route.query);
				this.filters = { ...filters };
				this.filters.includeCompletedOrders = parseInt(this.filters.includeCompletedOrders);
				this.searchFilters = { ...filters };
			}
			this.getOrdersInterval = setInterval(() => {
				if (this.filters.startDate === moment().format('YYYY-MM-DD')) {
					if (document.visibilityState === 'visible') {
						this.refresh();
					}
				}
			}, this.refreshTime);
			this.parseIntQueryParams();
			this.loaded = true;
			this.getOrders();
			this.emitter.on('onNewOrderReceived', () => {
				this.onNewOrderReceived();
			});
			this.emitter.on('updateLocalOrder', (payload) => {
				this.updateLocalOrder(payload);
			});
		},
		watch: {
			selectedRestaurants() {
				if (!this.$store.getters.token) return;
				this.searchFilters = { ...this.searchFilters, businessIDs: this.selectedRestaurants.join('-') };
				this.getOrders();
			},
		},
	};
</script>
